import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import { useAppDispatch } from '../../../hooks/redux';
import { fetchResetPasswordByToken, fetchResetPasswordEmail } from '../../../services';
import { getPasswordStyle } from './passwordResetUtils';
import { PasswordResetState } from '../../interfaces';

export const usePasswordReset = (email?: string | null): PasswordResetState => {
  const dispatch = useAppDispatch();
  const [tokenSent, setTokenSent] = useState(false);
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [timer, setTimer] = useState(120);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [passwordStyle, setPasswordStyle] = useState<React.CSSProperties>({});
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [intervalId]);

  const startTimer = useCallback(() => {
    if (intervalId) {
      clearInterval(intervalId);
    }

    setTimer(120);
    const id = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    setIntervalId(id);
  }, [intervalId]);

  const handleSendResetEmail = useCallback(async () => {
    if (!email) {
      message.error('Registered email not found.');
      return;
    }

    try {
      setResetPasswordLoading(true);
      await dispatch(fetchResetPasswordEmail({ email })).unwrap();
      message.success('Reset code has been sent to your email.');
      setTokenSent(true);
      setToken('');
      setNewPassword('');
      setConfirmPassword('');
      startTimer();
    } catch (error: any) {
      message.error(error.message || 'Error sending reset code.');
    } finally {
      setResetPasswordLoading(false);
    }
  }, [dispatch, email, startTimer]);

  const handleChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    const password = event.target.value;
    setNewPassword(password);
    setPasswordStyle(getPasswordStyle(password));
  };

  const handlePasswordResetByToken = useCallback(async () => {
    if (!email) {
      message.error('Registered email not found.');
      return;
    }

    if (!token) {
      message.error('Please enter the code you received.');
      return;
    }

    if (newPassword !== confirmPassword) {
      message.error('Passwords do not match.');
      return;
    }

    try {
      setResetPasswordLoading(true);
      await dispatch(fetchResetPasswordByToken({ token, newPassword, email })).unwrap();
      message.success('Password has been successfully reset.');
      setToken('');
      setNewPassword('');
      setConfirmPassword('');
      setTokenSent(false);
      setTimer(120);
      if (intervalId) {
        clearInterval(intervalId);
      }
    } catch (error: any) {
      message.error(error.message || 'Invalid or expired code.');
    } finally {
      setResetPasswordLoading(false);
    }
  }, [dispatch, token, newPassword, confirmPassword, email, intervalId]);

  return {
    confirmPassword,
    handleChangePassword,
    handlePasswordResetByToken,
    handleSendResetEmail,
    newPassword,
    passwordStyle,
    resetPasswordLoading,
    setConfirmPassword,
    setToken,
    timer,
    token,
    tokenSent,
  };
};
